// Stripe Supported Tax ID types: https://docs.stripe.com/tax/checkout/tax-ids#supported-types

type TaxIdValidationResult = {
  isValid: boolean;
  type?: string;
};

type ValidationRule = {
  pattern: RegExp;
  type: string;
};

const validationRules: ValidationRule[] = [
  // Andorra: Andorran NRT number
  { pattern: /^A-\d{6}-[A-Z]$/, type: 'ad_nrt' },
  // Argentina: Argentinian tax ID number
  { pattern: /^\d{2}-\d{7,8}-\d{1}$/, type: 'ar_cuit' },
  // Australia: Australian Business Number (AU ABN)
  { pattern: /^\d{11}$/, type: 'au_abn' },
  // Australia: Australian Taxation Office Reference Number
  { pattern: /^\d{12}$/, type: 'au_arn' },
  // Austria: European VAT number
  { pattern: /^ATU\d{8}$/, type: 'eu_vat' },
  // Belgium: European VAT number
  { pattern: /^BE0\d{9}$/, type: 'eu_vat' },
  // Bolivia: Bolivian tax ID
  { pattern: /^\d{9}$/, type: 'bo_tin' },
  // Brazil: Brazilian CNPJ number
  { pattern: /^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/, type: 'br_cnpj' },
  // Brazil: Brazilian CPF number
  { pattern: /^\d{3}\.\d{3}\.\d{3}-\d{2}$/, type: 'br_cpf' },
  // Bulgaria: Bulgaria Unified Identification Code
  { pattern: /^\d{9}$/, type: 'bg_uic' },
  // Bulgaria: European VAT number
  { pattern: /^BG\d{9,10}$/, type: 'eu_vat' },
  // Canada: Canadian BN
  { pattern: /^\d{9}$/, type: 'ca_bn' },
  // Canada: Canadian GST/HST number
  { pattern: /^\d{9}RT\d{4}$/, type: 'ca_gst_hst' },
  // Canada: Canadian PST number (British Columbia)
  { pattern: /^PST-\d{4}-\d{4}$/, type: 'ca_pst_bc' },
  // Canada
  { pattern: /^\d{6}-\d{1}$/, type: 'ca_pst_mb' },
  // Canada
  { pattern: /^\d{7}$/, type: 'ca_pst_sk' },
  // Canada
  { pattern: /^\d{10}TQ\d{4}$/, type: 'ca_qst' },
  // Chile: Chilean TIN
  { pattern: /^\d{2,3}\.\d{3}\.\d{3}-[A-Z]$/, type: 'cl_tin' },
  // China: Chinese tax ID
  { pattern: /^\d{15,18}$/, type: 'cn_tin' },
  // Colombia: Colombian NIT number
  { pattern: /^\d{3}\.\d{3}\.\d{3}-\d{1}$/, type: 'co_nit' },
  // Costa Rica: Costa Rican tax ID
  { pattern: /^\d{1}-\d{3}-\d{6}$/, type: 'cr_tin' },
  // Croatia: European VAT number
  { pattern: /^HR\d{11,12}$/, type: 'eu_vat' },
  // Cyprus: European VAT number
  { pattern: /^CY\d{8}[A-Z]$/, type: 'eu_vat' },
  // Czech Republic: European VAT number
  { pattern: /^CZ\d{10}$/, type: 'eu_vat' },
  // Denmark: European VAT number
  { pattern: /^DK\d{8}$/, type: 'eu_vat' },
  // Dominican Republic: Dominican RCN number
  { pattern: /^\d{3}-\d{7}-\d{1}$/, type: 'do_rcn' },
  // Ecuador: Ecuadorian RUC number
  { pattern: /^\d{9}0001$/, type: 'ec_ruc' },
  // Egypt: Egyptian Tax Identification Number
  { pattern: /^\d{9}$/, type: 'eg_tin' },
  // El Salvador: El Salvadorian NIT number
  { pattern: /^\d{4}-\d{6}-\d{3}-\d{1}$/, type: 'sv_nit' },
  // Estonia: European VAT number
  { pattern: /^EE\d{9}$/, type: 'eu_vat' },
  // EU: European One Stop Shop VAT number for non-Union scheme
  { pattern: /^EU\d{9,12}$/, type: 'eu_oss_vat' },
  // Finland: European VAT number
  { pattern: /^FI\d{8}$/, type: 'eu_vat' },
  // France: European VAT number
  { pattern: /^FR[A-Z]{2}\d{9}$/, type: 'eu_vat' },
  // Georgia: Georgian VAT
  { pattern: /^\d{9}$/, type: 'ge_vat' },
  // Germany: European VAT number
  { pattern: /^DE\d{9}$/, type: 'eu_vat' },
  // Greece: European VAT number
  { pattern: /^EL\d{9}$/, type: 'eu_vat' },
  // Hong Kong: Hong Kong BR number
  { pattern: /^\d{8}$/, type: 'hk_br' },
  // Hungary: European VAT number
  { pattern: /^HU\d{8}$/, type: 'eu_vat' },
  // Hungary: Hungary tax number (adószám)
  { pattern: /^\d{8}-\d{1}-\d{2}$/, type: 'hu_tin' },
  // Iceland: Icelandic VAT
  { pattern: /^\d{6}$/, type: 'is_vat' },
  // India: Indian GST number
  { pattern: /^\d{2}[A-Z]{5}\d{4}[A-Z]{3}$/, type: 'in_gst' },
  // Indonesia: Indonesian NPWP number
  { pattern: /^\d{2}\.\d{3}\.\d{3}\.\d{1}-\d{3}\.\d{3}$/, type: 'id_npwp' },
  // Ireland: European VAT number
  { pattern: /^IE\d{7}[A-Z]{2}$/, type: 'eu_vat' },
  // Israel
  { pattern: /^\d{9}$/, type: 'il_vat' },
  // Italy
  { pattern: /^IT\d{11}$/, type: 'eu_vat' },
  // Japan
  { pattern: /^\d{13}$/, type: 'jp_cn' },
  // Japan
  { pattern: /^\d{5}$/, type: 'jp_rn' },
  // Japan
  { pattern: /^T\d{13}$/, type: 'jp_trn' },
  // Kenya
  { pattern: /^P\d{9}[A-Z]$/, type: 'ke_pin' },
  // Latvia
  { pattern: /^LV\d{11,12}$/, type: 'eu_vat' },
  // Liechtenstein
  { pattern: /^CHE\d{9}$/, type: 'li_uid' },
  // Lithuania
  { pattern: /^LT\d{9,12}$/, type: 'eu_vat' },
  // Luxembourg
  { pattern: /^LU\d{8}$/, type: 'eu_vat' },
  // Malaysia
  { pattern: /^\d{8}$/, type: 'my_frp' },
  // Malaysia
  { pattern: /^[A-Z]\s\d{10}$/, type: 'my_itn' },
  // Malaysia
  { pattern: /^[A-Z]{2}\d{2}-\d{4}-\d{8}$/, type: 'my_sst' },
  // Malta
  { pattern: /^MT\d{8}$/, type: 'eu_vat' },
  // Mexico
  { pattern: /^[A-Z]{3}\d{6}\d{3}[A-Z]{2}\d{1}$/, type: 'mx_rfc' },
  // Netherlands
  { pattern: /^NL\d{9}B\d{2}$/, type: 'eu_vat' },
  // New Zealand
  { pattern: /^\d{9}$/, type: 'nz_gst' },
  // Norway
  { pattern: /^\d{9}MVA$/, type: 'no_vat' },
  // Norway
  { pattern: /^\d{7}$/, type: 'no_voec' },
  // Peru
  { pattern: /^\d{11}$/, type: 'pe_ruc' },
  // Philippines
  { pattern: /^\d{12}$/, type: 'ph_tin' },
  // Poland
  { pattern: /^PL\d{10}$/, type: 'eu_vat' },
  // Portugal
  { pattern: /^PT\d{9}$/, type: 'eu_vat' },
  // Romania
  { pattern: /^RO\d{10}$/, type: 'eu_vat' },
  // Romania
  { pattern: /^\d{13}$/, type: 'ro_tin' },
  // Russia
  { pattern: /^\d{10}$/, type: 'ru_inn' },
  // Russia
  { pattern: /^\d{9}$/, type: 'ru_kpp' },
  // Saudi Arabia
  { pattern: /^\d{15}$/, type: 'sa_vat' },
  // Serbia
  { pattern: /^\d{9}$/, type: 'rs_pib' },
  // Singapore
  { pattern: /^M\d{8}X$/, type: 'sg_gst' },
  // Singapore
  { pattern: /^\d{9}[A-Z]$/, type: 'sg_uen' },
  // Slovakia
  { pattern: /^SK\d{10}$/, type: 'eu_vat' },
  // Slovenia
  { pattern: /^SI\d{8}$/, type: 'eu_vat' },
  // Slovenia
  { pattern: /^\d{8}$/, type: 'si_tin' },
  // South Africa
  { pattern: /^\d{10}$/, type: 'za_vat' },
  // South Korea
  { pattern: /^\d{3}-\d{2}-\d{5}$/, type: 'kr_brn' },
  // Spain
  { pattern: /^[A-Z]\d{8}$/, type: 'es_cif' },
  // Spain
  { pattern: /^ESA\d{7}[A-Z]$/, type: 'eu_vat' },
  // Sweden
  { pattern: /^SE\d{12}$/, type: 'eu_vat' },
  // Switzerland
  { pattern: /^CHE-\d{3}\.\d{3}\.\d{3} MWST$/, type: 'ch_vat' },
  // Taiwan
  { pattern: /^\d{8}$/, type: 'tw_vat' },
  // Thailand
  { pattern: /^\d{13}$/, type: 'th_vat' },
  // Turkey
  { pattern: /^\d{10}$/, type: 'tr_tin' },
  // Ukraine
  { pattern: /^\d{9}$/, type: 'ua_vat' },
  // United Arab Emirates
  { pattern: /^\d{15}$/, type: 'ae_trn' },
  // United Kingdom
  { pattern: /^XI\d{9}$/, type: 'eu_vat' },
  // United Kingdom
  { pattern: /^GB\d{9}$/, type: 'gb_vat' },
  // United States
  { pattern: /^\d{2}-\d{7}$/, type: 'us_ein' },
  // Uruguay
  { pattern: /^\d{12}$/, type: 'uy_ruc' },
  // Venezuela
  { pattern: /^[A-Z]-\d{8}-\d{1}$/, type: 've_rif' },
  // Vietnam
  { pattern: /^\d{10}$/, type: 'vn_tin' },
];

function validateTaxId(input: string): TaxIdValidationResult {
  for (const rule of validationRules) {
    if (rule.pattern.test(input)) {
      return { isValid: true, type: rule.type };
    }
  }
  return { isValid: false };
}

export default validateTaxId;
